import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Check, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  overline: { es: "Newsletter Ritual", en: "Ritual Newsletter", va: "Newsletter Ritual" },
  title: { es: "Nuevos cafés y lanzamientos, antes que nadie", en: "New coffees and launches, before anyone else", va: "Nous cafés i llançaments, abans que ningú" },
  subtitle: {
    es: "Suscríbete y recibe novedades de origen, experiencias y ediciones limitadas. Sin spam, solo café del bueno.",
    en: "Subscribe to get news on origins, experiences and limited editions. No spam, just great coffee.",
    va: "Subscriu-te i rep novetats d'origen, experiències i edicions limitades. Sense spam, només café del bo.",
  },
  placeholder: { es: "tu@email.com", en: "your@email.com", va: "el-teu@email.com" },
  button: { es: "Suscribirme", en: "Subscribe", va: "Subscriure'm" },
  success: { es: "¡Bienvenido a la familia Ritual!", en: "Welcome to the Ritual family!", va: "Benvingut a la família Ritual!" },
  note: { es: "Al suscribirte aceptas nuestra Política de Privacidad. Puedes darte de baja cuando quieras.", en: "By subscribing you accept our Privacy Policy. You can unsubscribe anytime.", va: "En subscriure't acceptes la nostra Política de Privacitat. Pots donar-te de baixa quan vulgues." },
  consent: { es: "Acepto recibir comunicaciones comerciales de Grupo Ritual y acepto la", en: "I agree to receive marketing communications from Grupo Ritual and accept the", va: "Accepte rebre comunicacions comercials de Grupo Ritual i accepte la" },
  consentLink: { es: "política de privacidad", en: "privacy policy", va: "política de privacitat" },
};

export const Newsletter = ({ source = "web", variant = "section" }) => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !consent) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), source, lang, consent: true });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t({ es: "No se pudo registrar el email.", en: "Could not save your email.", va: "No s'ha pogut registrar l'email." }));
    } finally {
      setLoading(false);
    }
  };

  const ConsentLabel = ({ id, dark }) => (
    <label htmlFor={id} className={`flex items-start gap-2 cursor-pointer text-xs leading-snug ${dark ? "text-[#9A8F88]" : "text-[#9A8F88]"}`}>
      <Checkbox id={id} data-testid={`${id}`} checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-[#9A8F88] data-[state=checked]:bg-[#B85C38] data-[state=checked]:border-[#B85C38]" />
      <span>
        {t(COPY.consent)}{" "}
        <Link to="/legal/privacidad" target="_blank" className={`underline underline-offset-2 ${dark ? "text-[#E3DCD2] hover:text-white" : "text-[#B85C38] hover:text-[#1A1514]"}`}>{t(COPY.consentLink)}</Link>.
      </span>
    </label>
  );

  // Compact footer version (dark background)
  if (variant === "footer") {
    return (
      <div data-testid={`newsletter-footer`}>
        {done ? (
          <div data-testid="newsletter-footer-success" className="flex items-center gap-2 text-[#E3DCD2]">
            <Check className="w-4 h-4 text-[#B85C38]" /> <span className="text-sm">{t(COPY.success)}</span>
          </div>
        ) : (
          <div>
            <form onSubmit={submit} className="flex items-center bg-white/5 border border-white/15 rounded-full overflow-hidden focus-within:border-[#B85C38] transition-colors">
              <Mail className="w-4 h-4 text-[#9A8F88] ml-4 shrink-0" />
              <input
                data-testid="newsletter-footer-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t(COPY.placeholder)}
                className="flex-1 bg-transparent text-sm text-[#FBF9F6] placeholder:text-[#9A8F88] px-3 py-3 outline-none min-w-0"
              />
              <button
                data-testid="newsletter-footer-submit"
                type="submit"
                disabled={loading || !consent}
                aria-label={t(COPY.button)}
                className="shrink-0 m-1 w-9 h-9 rounded-full bg-[#B85C38] hover:bg-[#a04e2f] text-white flex items-center justify-center transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
            <div className="mt-3">
              <ConsentLabel id="newsletter-footer-consent" dark />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full premium section (light)
  return (
    <section data-testid="newsletter-section" className="relative overflow-hidden bg-[#2A2320] text-[#FBF9F6]">
      <div className="ritual-grain" />
      <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs tracking-overline uppercase text-[#B85C38]">
            <span className="w-6 h-px bg-current opacity-60" />{t(COPY.overline)}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-4 max-w-md leading-tight">{t(COPY.title)}</h2>
          <p className="text-[#E3DCD2] mt-4 max-w-md leading-relaxed">{t(COPY.subtitle)}</p>
        </div>
        <div className="lg:justify-self-end w-full max-w-md">
          {done ? (
            <div data-testid="newsletter-success" className="flex items-center gap-3 bg-white/5 border border-white/15 rounded-sm px-5 py-5">
              <div className="w-10 h-10 rounded-full bg-[#6B705C]/30 flex items-center justify-center shrink-0"><Check className="w-5 h-5 text-[#B85C38]" /></div>
              <p className="font-medium">{t(COPY.success)}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t(COPY.placeholder)}
                  className="bg-white/5 border-white/15 text-[#FBF9F6] placeholder:text-[#9A8F88] h-12 rounded-sm focus-visible:ring-[#B85C38]"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={loading || !consent}
                  className="inline-flex items-center justify-center gap-2 bg-[#FBF9F6] text-[#1A1514] hover:bg-[#E3DCD2] rounded-sm h-12 px-6 font-medium whitespace-nowrap transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t(COPY.button)} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
              <ConsentLabel id="newsletter-consent" dark />
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
